import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import CButton from "components/CButton";
import useRegisterForm, { RegisterFormInputs } from "./useRegisterForm";
import CTermsDialog from "components/CTermsDialog";
import CAddressAutocomplete from "components/CAddressAutocomplete";
import {
  Box,
  Link,
  List,
  Button,
  Checkbox,
  MenuItem,
  useTheme,
  Container,
  Typography,
  useMediaQuery,
  Grid,
  CircularProgress,
} from "@mui/material";
import CDialog from "components/CDialog";
import CLoader from "components/CLoader";
import parseMoney from "utils/parseMoney";
import { passwordMsgHelper } from "CONST";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import CTextField from "components/CTextField";
import HTMLtoolTip from "components/HTMLtoolTip";
import CNumberField from "components/CNumberField";
import CPasswordField from "components/CPasswordField";
import ConfirmValuesDialog from "./ConfirmValuesDialog";
import { useInvite } from "context/Invite/useInviteContext";
import FortunaInformation from "components/FortunaInformation";
import CVerificationDialog from "components/CVerificationDialog";
import useUser from "context/UserCustomer/useUserCustomerContext";
import CCommunicationsDialog from "components/CCommunicationsDialog";
import LoanOfficerInformation from "components/LoanOfficerInformation";
import { usePrivateLabel } from "context/PrivateLabelContext/UsePrivateLabelContextProvider";
import { OccupancyItem } from "typedef/PrivateLabel";
import LaduInformation from "components/LaduInformation";
import moment from "moment";
import blueSageValidateLicense, {
  useDebounce,
} from "screens/BorrowerInviteRegisterForm/blueSageValidateLicense";
import { MasterLoanOfficer } from "screens/BorrowerInviteRegisterForm";
import FormCoborrower from "./FormCoborrower";
import dollarFormatter from "utils/FormatterDollar";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MetaPixel from "./MetaPixel";
import PrivateLabelCheckboxes, {
  SignUpCheckbox,
} from "components/PrivateLabelCheckboxes/PrivateLabelCheckboxes";
import { usStates } from "assets/States";
import CDatePicker from "./CDatePicker";
import CustomBanner from "./CustomBanner";
import { useIsHomebridge } from "utils/useIsHomebridge";
import JGwentworthInformation from "components/JGwentworth";
import TrussfinancialInformation from "components/Trussfinancial";

type CreditScoreOption = {
  label: string;
  value: number | string;
};

export const checkboxLabels = [
  "Salary or hourly wages",
  "Self Employment",
  "Social security, pension, disability",
  "Rental Income",
  "Other",
];

const Divider = () => (
  <div
    style={{
      borderBottom: "1px solid lightgrey",
      margin: "30px 0",
    }}
  />
);

const Form: React.FC = () => {
  const [creditScoreOptions, setCreditScoreOptions] = useState<
    CreditScoreOption[]
  >([]);
  const user = useUser();
  const theme = useTheme();
  const invite = useInvite();
  const form = useRegisterForm();
  const { setPLMasterLoanOfficer } = form;
  const navigate = useNavigate();
  const { privateLabel } = usePrivateLabel();
  const [errorMessage, setErrorMessage] = useState(false);
  const [errorMessageManualAddress, setErrorMessageManualAddress] =
    useState(false);
  const [loadingBSApi, setLoadingBSApi] = useState(false);
  const [loadingBSApiManualAddress, setLoadingBSApiManualAddress] =
    useState(false);
  const { isHomeBridge } = useIsHomebridge();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const apiOfferCode = searchParams.get("apiOfferCode");
  const pathName = location.pathname;
  const isLOLink = pathName.includes("/invite/");
  const params = useParams();
  const isABroker = params["brokerName"];
  const sourceId = params["sourceId"];
  const control = form.control;
  const setValue = form.setValue;
  const getValues = form.getValues;
  const occupancy = form.watch("occupancy");
  const addressStreet = form.watch("propertyAddress.street_line");
  const addressState = form.watch("propertyAddress.state");
  const state = addressState ? addressState : form.watch("manual_state");
  const michiganState = state === "MI";
  const allowHomeImprovementForMichiganEnabled =
    privateLabel?.borrowerPortalConfiguration?.allowHomeImprovementForMichigan;
  const isHomeImprovementDisabled =
    allowHomeImprovementForMichiganEnabled === false && michiganState;
  const disabledField = invite && invite?.inviteType === "one_use";
  const loInviteScrollDown =
    invite && invite?.inviteType === "prefilled_lo_link";
  const welcomeGreetingText =
    privateLabel?.borrowerPortalConfiguration.customSignUpPageContent
      ?.welcomeGreeting;
  const callToActionText =
    privateLabel?.borrowerPortalConfiguration?.customSignUpPageContent
      ?.callToAction ?? "";

  useEffect(() => {
    if (loInviteScrollDown) {
      setTimeout(() => {
        const formBody = document.querySelector('[data-cy="form-body"]');
        if (formBody) {
          formBody.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
        }
      }, 150);
    }
  }, [loInviteScrollDown]);

  const renderCallToAction = () => {
    return callToActionText.split("\n").map((line, index) => (
      <Fragment key={index}>
        {line}
        {index < callToActionText.split("\n").length - 1 && <br />}
      </Fragment>
    ));
  };

  useEffect(() => {
    if (michiganState && !disabledField && !getValues("useProceeds")) {
      setValue("useProceeds", "");
    }
  }, [disabledField, michiganState, form, getValues, setValue]);

  const borrowerStreet = form.watch("borrowerAddress.street_line");
  const showBorrowerAddress = occupancy && occupancy !== "Primary Residence";
  const asCaOcuppancyType =
    form.getValues().propertyAddress?.state === "CA" ||
    form.watch("manual_state") === "CA";
  const isCornerstonePL =
    privateLabel?.id === "PRIVATE#LABEL#test.heloc.houseloan.com" ||
    privateLabel?.id === "PRIVATE#LABEL#heloc.houseloan.com";
  const occupancyTypes = privateLabel?.occupancyTypesEnabled?.map(
    (value) =>
      ({
        value: value,
        label: value,
      }) as OccupancyItem,
  );

  const { errors } = form.formState;
  const userAlreadyExists = form.errorCode === "EMAIL_IN_USE";

  const organizationName = privateLabel
    ? privateLabel.lenderName
    : `NFTYDoor's`;

  const streetLine = form.watch("propertyAddress.street_line");
  const [addCoBorrower, setAddCoBorrower] = useState(false);

  const incomeFields = [
    "salaryIncome",
    "selfEmploymentIncome",
    "socialIncome",
    "rentalIncome",
    "otherIncome",
  ] as const;

  const handleCheckboxChange = (
    checkbox: keyof typeof form.showIncomeInputs,
  ) => {
    form.setShowIncomeInputs((prev) => {
      const newState = { ...prev, [checkbox]: !prev[checkbox] };

      const borrowerField = `${checkbox}` as keyof typeof form.borrowerIncomes;
      if (!newState[checkbox]) {
        setValue(borrowerField, undefined);
        form.clearErrors(borrowerField);
        if (borrowerField === "salaryIncome") {
          setValue("employerNameBorrower", undefined);
          setValue("startDateBorrower", undefined);
          form.clearErrors("employerNameBorrower");
          form.clearErrors("startDateBorrower");
        }
        handleInputChange(
          { target: { value: "0" } } as React.ChangeEvent<HTMLInputElement>,
          borrowerField,
        );
      } else {
        setValue(borrowerField, "");
      }

      return newState;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: keyof typeof form.borrowerIncomes,
  ) => {
    const { value } = e.target;
    const numericValue = value.replace(/[$,\s]/g, "");
    const numberValue = parseFloat(numericValue);

    form.setBorrowerIcomes((prev) => ({
      ...prev,
      [name]: isNaN(numberValue) ? 0 : numberValue,
    }));
  };
  const totalAnnualIncome = Object.values(form.borrowerIncomes).reduce(
    (a, b) => Number(a) + Number(b),
    0,
  );

  const incomeFieldsCoborrower = [
    "salaryIncomeCoborrower",
    "selfEmploymentIncomeCoborrower",
    "socialIncomeCoborrower",
    "rentalIncomeCoborrower",
    "otherIncomeCoborrower",
  ] as const;

  const handleInputChangeCoborrower = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: keyof typeof form.coborrowerIncomes,
  ) => {
    const { value } = e.target;
    const numericValue = value.replace(/[$,\s]/g, "");
    const numberValue = parseFloat(numericValue);
    form.setCoborrowerIncomes((prev) => ({
      ...prev,
      [name]: isNaN(numberValue) ? 0 : numberValue,
    }));
  };

  const handleCheckboxChangeCoborrower = (
    checkbox: keyof typeof form.showIncomeInputsCoborrower,
  ) => {
    form.setShowIncomeInputsCoborrower((prev) => {
      const newState = { ...prev, [checkbox]: !prev[checkbox] };
      if (!newState[checkbox]) {
        const coborrowerField =
          `${checkbox}` as keyof typeof form.coborrowerIncomes;
        setValue(coborrowerField, "");
        handleInputChangeCoborrower(
          { target: { value: "0" } } as React.ChangeEvent<HTMLInputElement>,
          coborrowerField,
        );
      }
      return newState;
    });
  };

  const deleteCoborrower = () => {
    setAddCoBorrower(false);
    setValue("addCoborrower", "No");
    setValue("legalFirstNameCoborrower", undefined);
    setValue("legalLastNameCoborrower", undefined);
    setValue("legalMiddleNameCoborrower", undefined);
    setValue("legalSuffixCoborrower", undefined);
    setValue("maritalStatusCoborrower", undefined);
    setValue("creditScoreCoborrower", undefined);
    setValue("phoneCoborrower", undefined);
    setValue("confirmEmailAddressCoborrower", undefined);
    setValue("salaryIncomeCoborrower", undefined);
    setValue("selfEmploymentIncomeCoborrower", undefined);
    setValue("socialIncomeCoborrower", undefined);
    setValue("rentalIncomeCoborrower", undefined);
    setValue("otherIncomeCoborrower", undefined);
    setValue("startDateCoborrower", undefined);
    setValue("employerNameCoborrower", undefined);
    setValue("totalAnnualIncomeCoborrower", undefined);
    form.clearErrors("totalAnnualIncomeCoborrower");
  };

  const totalAnnualIncomeCoborrower = Object.values(
    form.coborrowerIncomes,
  ).reduce((a, b) => a + b, 0);

  const signUpCheckboxes: SignUpCheckbox[] = Object?.entries(
    privateLabel?.borrowerPortalConfiguration?.signUpCheckboxes || [],
  ).map(([key, value]) => ({
    text: value?.text,
    isRequired: value?.isRequired,
    key: value?.key,
  }));

  const requiredCheckboxesKeys = signUpCheckboxes
    ?.filter((checkbox) => checkbox?.isRequired && checkbox?.key)
    ?.map((checkbox) => `signUpCheckbox_${checkbox?.key}` as const);

  const allRequiredCheckboxesChecked = requiredCheckboxesKeys?.every((key) => {
    const value = form?.watch(key as keyof RegisterFormInputs);
    return typeof value === "boolean" ? value : false;
  });

  const disableSubmit =
    !allRequiredCheckboxesChecked ||
    (form.isInvalidStateId as boolean) ||
    totalAnnualIncome <= 0 ||
    errorMessage ||
    errorMessageManualAddress ||
    form.restrictedState;

  const oldValidations =
    !allRequiredCheckboxesChecked ||
    (form.isInvalidStateId as boolean) ||
    totalAnnualIncome <= 0 ||
    errorMessage ||
    errorMessageManualAddress;

  const messageRestrictedState =
    form.restrictedState && !oldValidations
      ? state === "UT" || state === "NV" || state === "NY"
        ? "We are unable to lend in this state at this time."
        : ""
      : undefined;

  const pricingEngine = form.pricingEngine;

  const creditScoreWatcher = form.watch("creditScore");

  useEffect(() => {
    const options = pricingEngine?.rangesCreditScore.map(
      (range, index, array) => {
        const fullRange = `${range.min} - ${range.max}`;
        if (index === array.length - 1) {
          return { label: "I don't know", value: range?.max };
        }
        return { label: fullRange, value: fullRange };
      },
    );
    setCreditScoreOptions(options ?? []);
  }, [pricingEngine, creditScoreWatcher]);

  const propertyAddressValue = form.watch("propertyAddress");
  const propertyAddressManualAddress = form.watch("manual_state");
  const debounceManualAddress = useDebounce(propertyAddressManualAddress, 1000);
  const loanOfficerId = invite?.loanOfficerId;
  const fastPass = invite?.fastPass;
  const allowCustomBanner =
    privateLabel?.borrowerPortalConfiguration?.customHomePageContent
      ?.allowBorrowerRegisterFormCustomBanner;
  const isLoggedIn = form.getValues("isLoggedIn");
  const getBluesageValidationLicense = useCallback(async () => {
    setPLMasterLoanOfficer(undefined);
    if (
      (!privateLabel?.isNFTYDoor || (isHomeBridge && isLOLink)) &&
      (propertyAddressValue?.state || debounceManualAddress)
    ) {
      const valueToValidate =
        propertyAddressValue?.state || debounceManualAddress;
      if (loanOfficerId) {
        valueToValidate === propertyAddressValue?.state
          ? setLoadingBSApi(true)
          : setLoadingBSApiManualAddress(true);
        const result = await blueSageValidateLicense(
          valueToValidate?.toUpperCase() as string,
          loanOfficerId,
          privateLabel,
        );

        const isValidMasterLoanOfficerData = (data: MasterLoanOfficer) => {
          return ["sk", "NMLS", "firstName", "lastName", "licenseNumber"].every(
            (key) => key in data,
          );
        };

        if (!privateLabel?.isNFTYDoor && result?.data?.data) {
          const { id, ...rest } = result.data.data[0];
          if (isValidMasterLoanOfficerData(rest)) {
            setPLMasterLoanOfficer(rest as MasterLoanOfficer);
          }
        }

        if (
          result?.data?.message !== "Valid license" &&
          propertyAddressValue?.state
        ) {
          setErrorMessage(true);
          setErrorMessageManualAddress(false);
          setLoadingBSApi(false);
        } else if (
          result?.data?.message !== "Valid license" &&
          debounceManualAddress
        ) {
          setErrorMessageManualAddress(true);
          setErrorMessage(false);
          setLoadingBSApiManualAddress(false);
        } else {
          setErrorMessageManualAddress(false);
          setErrorMessage(false);
          setLoadingBSApi(false);
          setLoadingBSApiManualAddress(false);
        }
      }
    }
  }, [
    propertyAddressValue?.state,
    debounceManualAddress,
    loanOfficerId,
    setPLMasterLoanOfficer,
    privateLabel,
    isHomeBridge,
    isLOLink,
  ]);

  useEffect(() => {
    getBluesageValidationLicense();
  }, [getBluesageValidationLicense]);

  const defaultVerificationType =
    form?.checkPhoneValidation?.phoneTypeBorrower === "MOBILE" ||
    form?.checkPhoneValidation?.phoneTypeBorrower === "VOIP"
      ? "sms"
      : form?.checkPhoneValidation?.phoneTypeBorrower === "LANDLINE"
        ? "email"
        : "sms";

  const loadedAddCoborrower = useRef(false);

  useEffect(() => {
    if (invite?.addCoborrower && !loadedAddCoborrower.current) {
      setAddCoBorrower(true);
      setValue("addCoborrower", "Yes");
      loadedAddCoborrower.current = true;
    }
  }, [invite?.addCoborrower, setValue]);

  useEffect(() => {
    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField) {
      const firstField = Object.keys(errors)[0];
      const el = document.querySelector(`[name="${firstField}"]`);
      if (el) {
        (el as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        form.setFocus(firstField as keyof RegisterFormInputs);
      }
    }
  }, [errors, form]);

  const totalHouseholdIncome =
    Number(totalAnnualIncome ?? 0) + Number(totalAnnualIncomeCoborrower ?? 0);

  return (
    <Fragment>
      {sourceId === "ladu" && <MetaPixel />}
      {isABroker && <CLoader loading={invite ? false : true} />}
      <Container
        maxWidth="md"
        disableGutters={useMediaQuery(theme.breakpoints.only("xs"))}
      >
        <Fragment>
          <Box>
            <Typography
              mt={4}
              mb={5}
              variant="h1"
              lineHeight={1}
              color={
                isHomeBridge
                  ? theme.palette.common.white
                  : theme.palette.text.primary
              }
              letterSpacing={-0.8}
              px={{ xs: 2, md: 0 }}
              fontSize={{ xs: "3.5rem", md: "5rem" }}
              textAlign={
                sourceId === "jgwentworth" || sourceId === "trussfinancial"
                  ? "center"
                  : undefined
              }
            >
              {sourceId === "jgwentworth" || sourceId === "trussfinancial" ? (
                <>
                  Unlock your home's potential.
                  <br />
                  <Typography
                    variant="subtitle1"
                    fontSize={{ xs: "1.5rem", md: "1.8rem" }}
                    mt={2}
                    textAlign="center"
                  >
                    Let NFTYDoor help you get pre-qualified for a HELOC in just
                    minutes.
                  </Typography>
                </>
              ) : (
                <>
                  <span
                    style={{
                      color: isHomeBridge
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                    }}
                  >
                    {welcomeGreetingText}{" "}
                  </span>
                  {renderCallToAction()}
                </>
              )}
            </Typography>
          </Box>
          {allowCustomBanner &&
            (!invite?.loanOfficer?.firstName || apiOfferCode) && (
              <CustomBanner
                logoUrl={privateLabel?.mediaResources?.customBannerImage}
                customBannerText={
                  privateLabel?.borrowerPortalConfiguration
                    ?.customHomePageContent?.customBannerText
                }
                phoneNumberForOrganicFlow={
                  privateLabel?.borrowerPortalConfiguration
                    ?.customHomePageContent?.phoneNumberForOrganicFlow
                }
                NMLSID={privateLabel?.NMLScompany}
              />
            )}
          {invite?.loanOfficer?.firstName && !apiOfferCode && (
            <LoanOfficerInformation
              nmls={invite?.loanOfficer?.NMLSNumber}
              company={
                invite.broker?.lenderName ?? privateLabel?.legalLenderName
              }
              lastName={invite?.loanOfficer.lastName}
              avatar={invite.loanOfficer.avatar}
              companyNMLS={
                invite.broker?.NMLScompany ?? privateLabel?.NMLScompany
              }
              firstName={invite.loanOfficer?.firstName}
              isNFTYDoor={privateLabel?.isNFTYDoor}
              address={invite.loanOfficer?.address}
              phone={invite?.loanOfficer?.phone}
              title={"WELCOME! I'm glad you're here!"}
              subtitle={`Interested in accessing your home equity? We now offer Home Equity
          Loans through ${organizationName} digital lending platform. Simply fill out the items below and see your prequalification results instantly. There is no obligation or credit check at this stage of the process, only if you choose to accept and move forward!`}
            />
          )}
          {sourceId === "ladu" && <LaduInformation />}
          {""}
          {sourceId === "fortuna" && <FortunaInformation />}
          {""}
          {sourceId === "jgwentworth" && <JGwentworthInformation />}
          {sourceId === "trussfinancial" && <TrussfinancialInformation />}
          <Box
            data-cy="form-body"
            borderRadius={10}
            bgcolor={
              isHomeBridge && !sourceId
                ? theme.palette.background.default
                : theme.palette.background.paper
            }
            sx={{ scrollbarWidth: "none" }}
          >
            <Box px={{ xs: 2, md: 6 }} py={{ xs: 4, md: 5 }}>
              {((invite?.loanOfficer?.firstName && privateLabel?.isNFTYDoor) ||
                sourceId) && (
                <Typography
                  pb={4}
                  variant="h6"
                  display={"flex"}
                  alignSelf={"center"}
                  justifyContent={"center"}
                  fontFamily={"Pro Display Light"}
                >
                  Powered by{" "}
                  <span
                    style={{
                      fontFamily: "Pro Display Semibold",
                      marginLeft: 4,
                    }}
                  >
                    <span style={{ color: theme.palette.primary.main }}>
                      NFTY
                    </span>
                    Door.
                  </span>
                </Typography>
              )}
              <form onSubmit={form.onSubmit}>
                <Controller
                  name={"propertyAddress"}
                  control={control}
                  render={({ field }) => (
                    <>
                      {loadingBSApi && (
                        <CircularProgress
                          style={{ width: 16, height: 16, color: "grey" }}
                        />
                      )}
                      <CAddressAutocomplete
                        name="propertyAddressAutocomplete"
                        placeholder="Start typing your home address and please select from the dropdown."
                        address={field.value}
                        setAddress={(newAddress) => {
                          field.onChange(newAddress);
                          setValue("useProceeds", "");
                        }}
                        sx={{ mb: 3, mt: { xs: 2, md: 0 } }}
                        label="Subject Property Address"
                        disabled={disabledField}
                        //@ts-ignore
                        error={
                          errorMessage
                            ? "Your loan officer is not licensed in this state, please contact them for next steps"
                            : errors?.propertyAddress?.message
                        }
                        isInvalidStateId={form.isInvalidStateId}
                      />
                    </>
                  )}
                />
                {addressStreet === "My address is not listed" && (
                  <>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignItems={"flex-start"}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1}
                    >
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_street_number"
                          render={({ field }) => (
                            <CTextField
                              label="Street Number"
                              placeholder="Street number"
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_street_number?.message}
                            />
                          )}
                        />
                      </Box>
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_street_name"
                          render={({ field }) => (
                            <CTextField
                              label="Street Name"
                              placeholder="Street name"
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_street_name?.message}
                            />
                          )}
                        />
                      </Box>
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_street_suffix"
                          render={({ field }) => (
                            <CTextField
                              label="Street Suffix"
                              placeholder="Ln, Rd, St..."
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_street_suffix?.message}
                            />
                          )}
                        />
                      </Box>
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_apt"
                          render={({ field }) => (
                            <CTextField
                              label="Apt/Suite"
                              placeholder="Unit #"
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_apt?.message}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignItems={"flex-start"}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1}
                    >
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_city"
                          render={({ field }) => (
                            <CTextField
                              label="City"
                              placeholder="City"
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_city?.message}
                            />
                          )}
                        />
                      </Box>
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_state"
                          render={({ field }) => (
                            <>
                              <CTextField
                                label="State"
                                placeholder="VA, FL, TX..."
                                fullWidth
                                select
                                disabled={disabledField}
                                sx={{ mb: 3 }}
                                {...field}
                                error={
                                  errorMessageManualAddress
                                    ? "Your loan officer is not licensed in this state, please contact them for next steps"
                                    : errors?.manual_state?.message
                                }
                              >
                                {usStates?.map((item, index) => {
                                  const stateOption =
                                    typeof item === "string"
                                      ? { value: item, label: item }
                                      : item;
                                  return (
                                    <MenuItem
                                      value={stateOption.value}
                                      key={index}
                                    >
                                      {stateOption.label}
                                    </MenuItem>
                                  );
                                })}
                              </CTextField>
                              {loadingBSApiManualAddress && (
                                <CircularProgress
                                  style={{
                                    width: 16,
                                    height: 16,
                                    color: "grey",
                                  }}
                                />
                              )}
                            </>
                          )}
                        />
                        {streetLine === "My address is not listed" &&
                          form?.isInvalidStateId &&
                          !errors?.manual_state?.message && (
                            <Typography
                              mt={-2}
                              mb={2}
                              ml={1.5}
                              variant="body1"
                              lineHeight={1}
                              color={theme.palette.error.main}
                              fontSize={{ xs: "0.85rem", sm: "1.085rem" }}
                            >
                              {form?.isInvalidStateId}
                            </Typography>
                          )}
                      </Box>
                      <Box width={{ xs: "100%", md: "50%" }}>
                        <Controller
                          control={control}
                          name="manual_zipcode"
                          render={({ field }) => (
                            <CTextField
                              label="Zipcode"
                              placeholder="Zipcode"
                              fullWidth
                              autoComplete="off"
                              disabled={disabledField}
                              sx={{ mb: 3 }}
                              {...field}
                              error={errors?.manual_zipcode?.message}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </>
                )}
                <Controller
                  name={"occupancy"}
                  control={control}
                  render={({ field }) => (
                    <CTextField
                      select
                      disabled={disabledField}
                      fullWidth
                      tooltip={
                        <HTMLtoolTip
                          title={
                            <Fragment>
                              <Typography color="inherit">
                                Owner Occupied
                              </Typography>
                              {
                                "I/We will occupy the subject property as my/our principal residence within 60 days after the date of closing as required by, and in compliance with, the terms of the Deed of Trust/Mortgage/Security Instrument relating to the subject property. I/We will continue to occupy the property as my/our principal residence for at least one year after the date of occupancy, unless Lender otherwise agrees in writing or extenuating circumstances exist which are beyond my/our control."
                              }
                              <br />
                              <br />
                              <Typography color="inherit">
                                Occupied as a Second Home
                              </Typography>
                              {
                                "I/We will occupy the subject property as my/our second residence as required by, and in compliance with, the terms of the Deed of Trust/Mortgage/Security Instrument relating to the subject property."
                              }
                              <br />
                              <br />
                              <Typography color="inherit">
                                Investment Property - Will Not Occupy
                              </Typography>
                              {"I/We will not occupy the subject property."}
                            </Fragment>
                          }
                        >
                          <Button />
                        </HTMLtoolTip>
                      }
                      label="Occupancy Type"
                      placeholder="Select"
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.occupancy?.message}
                    >
                      {occupancyTypes?.map((item, index) => {
                        return (
                          <MenuItem value={item?.value} key={index}>
                            {item?.label}
                          </MenuItem>
                        );
                      })}
                    </CTextField>
                  )}
                />
                {showBorrowerAddress && (
                  <>
                    <Controller
                      name="borrowerAddress"
                      control={control}
                      render={({ field }) => (
                        <CAddressAutocomplete
                          {...field}
                          onChange={undefined}
                          placeholder="Please enter the address of the home you currently live in."
                          address={field.value}
                          disabled={disabledField}
                          setAddress={field.onChange}
                          sx={{ mb: 3, mt: { xs: 2, md: 0 } }}
                          label="Primary home address"
                          //@ts-ignore
                          error={errors?.borrowerAddress?.message}
                        />
                      )}
                    />
                    {borrowerStreet === "My address is not listed" && (
                      <>
                        <Box
                          width={"100%"}
                          display={"flex"}
                          alignItems={"flex-start"}
                          flexDirection={{ xs: "column", md: "row" }}
                          gap={1}
                        >
                          <Box width={{ xs: "100%", md: "50%" }}>
                            <Controller
                              control={control}
                              name="manual_borrower_street_number"
                              render={({ field }) => (
                                <CTextField
                                  label="Street Number"
                                  placeholder="Street number"
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={
                                    errors?.manual_borrower_street_number
                                      ?.message
                                  }
                                />
                              )}
                            />
                          </Box>
                          <Box width={{ xs: "100%", md: "50%" }}>
                            <Controller
                              control={control}
                              name="manual_borrower_street_name"
                              render={({ field }) => (
                                <CTextField
                                  label="Street Name"
                                  placeholder="Street name"
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={
                                    errors?.manual_borrower_street_name?.message
                                  }
                                />
                              )}
                            />
                          </Box>
                          <Box width={{ xs: "100%", md: "50%" }}>
                            <Controller
                              control={control}
                              name="manual_borrower_street_suffix"
                              render={({ field }) => (
                                <CTextField
                                  label="Street Suffix"
                                  placeholder="Ln, Rd, St..."
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={
                                    errors?.manual_borrower_street_suffix
                                      ?.message
                                  }
                                />
                              )}
                            />
                          </Box>
                          <Box width={{ xs: "100%", md: "50%" }}>
                            <Controller
                              control={control}
                              name="manual_borrower_apt"
                              render={({ field }) => (
                                <CTextField
                                  label="Apt/Suite"
                                  placeholder="Unit #"
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={errors?.manual_borrower_apt?.message}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                        <Box
                          width={"100%"}
                          display={"flex"}
                          alignItems={"flex-start"}
                          flexDirection={{ xs: "column", md: "row" }}
                        >
                          <Box width={{ xs: "100%", md: "50%" }}>
                            <Controller
                              control={control}
                              name="manual_borrower_city"
                              render={({ field }) => (
                                <CTextField
                                  label="City"
                                  placeholder="City"
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={errors?.manual_borrower_city?.message}
                                />
                              )}
                            />
                          </Box>
                          <Box
                            width={{ xs: "100%", md: "50%" }}
                            ml={{ xs: 0, md: 1 }}
                          >
                            <Controller
                              control={control}
                              name="manual_borrower_state"
                              render={({ field }) => (
                                <CTextField
                                  label="State"
                                  placeholder="VA, FL, TX..."
                                  fullWidth
                                  select
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={errors?.manual_borrower_state?.message}
                                >
                                  {usStates?.map((item, index) => {
                                    const stateOption =
                                      typeof item === "string"
                                        ? { value: item, label: item }
                                        : item;
                                    return (
                                      <MenuItem
                                        value={stateOption.value}
                                        key={index}
                                      >
                                        {stateOption.label}
                                      </MenuItem>
                                    );
                                  })}
                                </CTextField>
                              )}
                            />
                          </Box>
                          <Box
                            width={{ xs: "100%", md: "50%" }}
                            ml={{ xs: 0, md: 1 }}
                          >
                            <Controller
                              control={control}
                              name="manual_borrower_zipcode"
                              render={({ field }) => (
                                <CTextField
                                  label="Zipcode"
                                  placeholder="Zipcode"
                                  fullWidth
                                  autoComplete="off"
                                  disabled={disabledField}
                                  sx={{ mb: 3 }}
                                  {...field}
                                  error={
                                    errors?.manual_borrower_zipcode?.message
                                  }
                                />
                              )}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </>
                )}
                <Controller
                  control={control}
                  name="homeValue"
                  render={({ field }) => (
                    <CNumberField
                      fullWidth
                      label="Home Value"
                      placeholder="$200,000"
                      isNumericString
                      thousandSeparator
                      disabled={disabledField}
                      allowNegative={false}
                      allowEmptyFormatting={false}
                      prefix="$ "
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.homeValue?.message}
                      value={parseMoney(field.value)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="currentLoanBalance"
                  render={({ field }) => (
                    <CNumberField
                      fullWidth
                      label="Current Loan Balance"
                      placeholder="$100,000"
                      isNumericString
                      thousandSeparator
                      disabled={disabledField}
                      allowNegative={false}
                      allowEmptyFormatting={false}
                      prefix="$"
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.currentLoanBalance?.message}
                      value={parseMoney(field.value)}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="requestedNewLoan"
                  render={({ field }) => (
                    <CNumberField
                      fullWidth
                      label="Requested Loan Amount"
                      placeholder="$65,000"
                      isNumericString
                      thousandSeparator
                      disabled={disabledField}
                      allowNegative={false}
                      allowEmptyFormatting={false}
                      prefix="$ "
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.requestedNewLoan?.message}
                      value={parseMoney(field.value)}
                      onChange={(e) => {
                        form.setValue("requestedNewLoan", e.target.value, {
                          shouldValidate: true,
                        });
                      }}
                      decimalScale={0}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="useProceeds"
                  render={({ field }) => (
                    <CTextField
                      select
                      fullWidth
                      label="Use of Proceeds"
                      disabled={disabledField}
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.useProceeds?.message}
                    >
                      <MenuItem
                        value="Home Improvement"
                        disabled={isHomeImprovementDisabled}
                      >
                        Home Improvement
                      </MenuItem>
                      <MenuItem value="Debt Consolidation">
                        Debt Consolidation
                      </MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </CTextField>
                  )}
                />
                <Controller
                  control={control}
                  name={"creditScore"}
                  render={({ field }) => (
                    <CTextField
                      select
                      fullWidth
                      label="Credit Score"
                      disabled={disabledField}
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.creditScore?.message}
                    >
                      {creditScoreOptions?.map((option, index) => (
                        <MenuItem
                          key={`${index}${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </CTextField>
                  )}
                />

                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    mt={4}
                    mb={5}
                    variant="h2"
                    lineHeight={1.2}
                    color={theme.palette.text.primary}
                    letterSpacing={-0.8}
                    px={{ xs: 2, md: 0 }}
                    fontSize={{ xs: "1.5rem", md: "2rem" }}
                  >
                    Primary Borrower
                  </Typography>
                  {!addCoBorrower && (
                    <Button
                      data-cy="addCoborrower"
                      variant="text"
                      disabled={disabledField}
                      sx={{
                        textTransform: "none",
                      }}
                      onClick={() => {
                        setAddCoBorrower(!addCoBorrower);
                        setValue("addCoborrower", "Yes");
                        form.setShowIncomeInputsCoborrower({
                          salaryIncomeCoborrower: false,
                          selfEmploymentIncomeCoborrower: false,
                          socialIncomeCoborrower: false,
                          rentalIncomeCoborrower: false,
                          otherIncomeCoborrower: false,
                        });
                      }}
                    >
                      + Add Co-Borrower
                    </Button>
                  )}
                </Grid>

                <Box
                  width={"100%"}
                  display={"flex"}
                  alignItems={"flex-start"}
                  flexDirection={{ xs: "column", md: "row" }}
                >
                  <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => (
                        <CTextField
                          label="Legal First Name"
                          placeholder="John"
                          fullWidth
                          autoComplete="off"
                          disabled={disabledField || isLoggedIn}
                          sx={{ mb: 3 }}
                          {...field}
                          error={errors?.firstName?.message}
                        />
                      )}
                    />
                  </Box>

                  <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => (
                        <CTextField
                          label="Legal Last Name"
                          placeholder="Doe"
                          fullWidth
                          autoComplete="off"
                          disabled={disabledField || isLoggedIn}
                          sx={{ mb: 3 }}
                          {...field}
                          error={errors?.lastName?.message}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box
                  width={"100%"}
                  display={"flex"}
                  alignItems={"flex-start"}
                  flexDirection={{ xs: "column", md: "row" }}
                >
                  <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
                    <Controller
                      control={control}
                      name="middleName"
                      render={({ field }) => (
                        <CTextField
                          fullWidth
                          autoComplete="off"
                          label="Legal Middle Name (optional)"
                          sx={{ mb: 3 }}
                          {...field}
                          error={errors?.middleName?.message}
                        />
                      )}
                    />
                  </Box>
                  <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
                    <Controller
                      control={control}
                      name="suffix"
                      render={({ field }) => (
                        <CTextField
                          fullWidth
                          autoComplete="off"
                          label="Suffix (optional)"
                          sx={{ mb: 3 }}
                          {...field}
                          error={errors?.suffix?.message}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Controller
                  control={control}
                  name="maritalStatus"
                  render={({ field }) => (
                    <CTextField
                      select
                      fullWidth
                      label="Marital Status"
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.maritalStatus?.message}
                      inputProps={{
                        "data-cy": "b1MaritalStatus-select",
                      }}
                    >
                      <MenuItem data-cy="b1-Unmarried" value="Unmarried">
                        Unmarried
                      </MenuItem>
                      <MenuItem data-cy="b1-Married" value="Married">
                        Married
                      </MenuItem>
                      <MenuItem data-cy="b1-Separated" value="Separated">
                        Separated
                      </MenuItem>
                    </CTextField>
                  )}
                />
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <CNumberField
                      fullWidth
                      autoComplete="off"
                      label="Cell Phone"
                      disabled={disabledField || isLoggedIn}
                      placeholder="+1(123)-123-1234"
                      format={
                        process.env.REACT_APP_ENV === "prod"
                          ? "+1###############"
                          : "+###############"
                      }
                      sx={{ mb: 3 }}
                      allowEmptyFormatting
                      {...field}
                      error={errors?.phoneNumber?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <CTextField
                      label="Email Address"
                      disabled={disabledField || isLoggedIn}
                      placeholder="Type in here"
                      fullWidth
                      autoComplete="off"
                      sx={{ mb: 3 }}
                      {...field}
                      error={errors?.email?.message}
                    />
                  )}
                />
                <Divider />
                <Box>
                  <Typography
                    variant="h2"
                    lineHeight={1.2}
                    color={theme.palette.text.primary}
                    letterSpacing={-0.8}
                    px={{ xs: 2, md: 0 }}
                    fontSize={{ xs: "1.5rem", md: "2rem" }}
                  >
                    Annual Income
                  </Typography>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mb: 5 }}
                  >
                    Please provide more details
                  </Typography>

                  {incomeFields.map((incomeField, index) => (
                    <Box key={incomeField}>
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          checked={form.showIncomeInputs[incomeField]}
                          onChange={() => handleCheckboxChange(incomeField)}
                          data-cy={`incomeCheckboxB1-${incomeField}`}
                          disabled={fastPass}
                        />
                        <Typography>{checkboxLabels[index]}</Typography>
                      </Box>

                      {form.showIncomeInputs[incomeField] && (
                        <Box pl={5}>
                          <Controller
                            control={control}
                            name={incomeField}
                            render={({ field }) => (
                              <CNumberField
                                name={incomeField}
                                fullWidth
                                label={`$ Total Annual Compensation - ${checkboxLabels[index]}`}
                                placeholder="$200,000"
                                isNumericString
                                thousandSeparator
                                allowNegative={false}
                                allowEmptyFormatting={false}
                                prefix="$"
                                disabled={fastPass}
                                sx={{ mb: 1 }}
                                value={parseMoney(field.value ?? "")}
                                error={errors[incomeField]?.message}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  field.onChange(
                                    e.target.value
                                      .replace(/[$,\s]/g, "")
                                      .replace(/^0+(?=\d)/, ""),
                                  );
                                  handleInputChange(
                                    e,
                                    incomeField as
                                      | "salaryIncome"
                                      | "selfEmploymentIncome"
                                      | "socialIncome"
                                      | "rentalIncome"
                                      | "otherIncome",
                                  );
                                }}
                              />
                            )}
                          />
                          {incomeField === "salaryIncome" && (
                            <>
                              <Controller
                                control={control}
                                name="employerNameBorrower"
                                render={({ field }) => (
                                  <CTextField
                                    label="Primary Borrower’s Employer name"
                                    placeholder="Your Current Employer"
                                    fullWidth
                                    disabled={fastPass}
                                    autoComplete="off"
                                    sx={{ mb: 3, mt: 2 }}
                                    {...field}
                                    error={
                                      errors?.employerNameBorrower?.message
                                    }
                                  />
                                )}
                              />
                              <Controller
                                control={control}
                                name="startDateBorrower"
                                render={({ field }) => (
                                  <CDatePicker
                                    {...field}
                                    label="Start Date"
                                    value={
                                      field.value
                                        ? moment(field.value, "YYYY-MM-DD")
                                        : undefined
                                    }
                                    onChange={(newValue) => {
                                      field.onChange(
                                        newValue
                                          ? newValue.format("YYYY-MM-DD")
                                          : undefined,
                                      );
                                    }}
                                    disabled={fastPass}
                                    minDate={moment("1908-02-05")}
                                    maxDate={moment().subtract(0, "year")}
                                    errorMessage={
                                      errors?.startDateBorrower?.message
                                    }
                                  />
                                )}
                              />
                            </>
                          )}
                          {incomeField === "rentalIncome" && (
                            <>
                              <Controller
                                control={control}
                                name="rentalProperties"
                                render={({ field }) => (
                                  <CTextField
                                    label="Number of Rental Properties"
                                    placeholder="How many rental properties you own?"
                                    fullWidth
                                    disabled={fastPass}
                                    autoComplete="off"
                                    sx={{ mb: 3, mt: 2 }}
                                    {...field}
                                    data-cy="rentalPropertiesError"
                                    error={errors?.rentalProperties?.message}
                                  />
                                )}
                              />
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                  {errors.inputsGroup && (
                    <Typography
                      color={theme.palette.error.main}
                      fontWeight={400}
                      fontSize={"0.75rem"}
                    >
                      {errors.inputsGroup.message}
                    </Typography>
                  )}

                  <Box mt={3}>
                    <Typography variant="h6" color={theme.palette.primary.main}>
                      Total Annual Income:
                      {dollarFormatter.format(totalAnnualIncome ?? 0)}
                    </Typography>
                  </Box>
                  {errors.annualIncome && (
                    <Typography
                      color={theme.palette.error.main}
                      fontWeight={400}
                      fontSize={"0.75rem"}
                    >
                      {errors.annualIncome.message}
                    </Typography>
                  )}
                  <Divider></Divider>
                </Box>

                {addCoBorrower && (
                  <>
                    <FormCoborrower
                      control={control}
                      setValue={setValue}
                      disabledField={disabledField ?? false}
                      errors={errors}
                      deleteCoborrower={deleteCoborrower}
                      form={form}
                      options={creditScoreOptions}
                    />
                    <Box mb={2}>
                      <Typography
                        variant="h2"
                        lineHeight={1.2}
                        color={theme.palette.text.primary}
                        letterSpacing={-0.8}
                        px={{ xs: 2, md: 0 }}
                        fontSize={{ xs: "1.5rem", md: "2rem" }}
                      >
                        Annual Income
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        px={{ xs: 2, md: 0 }}
                      >
                        Please provide more details
                      </Typography>
                    </Box>
                    {incomeFieldsCoborrower.map((incomeField, index) => (
                      <Box key={incomeField}>
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            sx={{ pl: 0 }}
                            checked={
                              form.showIncomeInputsCoborrower[incomeField]
                            }
                            onChange={() =>
                              handleCheckboxChangeCoborrower(incomeField)
                            }
                            data-cy={`incomeCheckboxB2-${incomeField}`}
                          />
                          <Typography>{checkboxLabels[index]}</Typography>
                        </Box>
                        {form.showIncomeInputsCoborrower[incomeField] && (
                          <Box pl={5}>
                            <Controller
                              control={control}
                              name={incomeField}
                              render={({ field }) => (
                                <CNumberField
                                  fullWidth
                                  label={`$ Total Annual Compensation - ${checkboxLabels[index]}`}
                                  placeholder="$200,000"
                                  name={incomeField}
                                  isNumericString
                                  thousandSeparator
                                  disabled={disabledField}
                                  allowNegative={false}
                                  allowEmptyFormatting={false}
                                  prefix="$"
                                  sx={{ mb: 1 }}
                                  value={parseMoney(field.value ?? "")}
                                  error={errors[incomeField]?.message}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    field.onChange(
                                      e.target.value
                                        .replace(/[$,\s]/g, "")
                                        .replace(/^0+(?=\d)/, ""),
                                    );
                                    handleInputChangeCoborrower(e, incomeField);
                                  }}
                                />
                              )}
                            />
                            {index + 1 === 1 && (
                              <>
                                <Controller
                                  control={control}
                                  name="employerNameCoborrower"
                                  render={({ field }) => (
                                    <CTextField
                                      label="Employer Name"
                                      placeholder="Your Current Employer"
                                      fullWidth
                                      autoComplete="off"
                                      disabled={disabledField}
                                      sx={{ mb: 3, mt: 2 }}
                                      {...field}
                                      error={
                                        errors?.employerNameCoborrower?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  control={control}
                                  name="startDateCoborrower"
                                  render={({ field }) => (
                                    <CDatePicker
                                      {...field}
                                      label="Start Date"
                                      value={
                                        field.value
                                          ? moment(field.value, "YYYY-MM-DD")
                                          : undefined
                                      }
                                      onChange={(newValue) => {
                                        field.onChange(
                                          newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : undefined,
                                        );
                                      }}
                                      disabled={disabledField}
                                      minDate={moment("1908-02-05")}
                                      maxDate={moment().subtract(0, "year")}
                                      sx={{ mb: 3 }}
                                      errorMessage={
                                        errors?.startDateCoborrower?.message
                                      }
                                    />
                                  )}
                                />
                              </>
                            )}
                            {index + 1 === 4 && (
                              <>
                                <Controller
                                  control={control}
                                  name="rentalPropertiesCoborrower"
                                  render={({ field }) => (
                                    <CTextField
                                      label="Rental properties owned"
                                      placeholder="How many rental properties you own?"
                                      fullWidth
                                      disabled={fastPass}
                                      autoComplete="off"
                                      sx={{ mb: 3, mt: 2 }}
                                      {...field}
                                      error={
                                        errors?.rentalPropertiesCoborrower
                                          ?.message
                                      }
                                    />
                                  )}
                                />
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                    {errors.inputsGroupCoborrower && (
                      <Typography
                        color={theme.palette.error.main}
                        fontWeight={400}
                        fontSize={"0.75rem"}
                      >
                        {errors.inputsGroupCoborrower.message}
                      </Typography>
                    )}
                    <Box mt={3}>
                      <Typography
                        variant="h6"
                        color={theme.palette.primary.main}
                      >
                        Total Secondary Borrower Income:
                        {dollarFormatter.format(
                          totalAnnualIncomeCoborrower ?? 0,
                        )}
                      </Typography>
                    </Box>
                    {errors.totalAnnualIncomeCoborrower && (
                      <Typography
                        color={theme.palette.error.main}
                        fontWeight={400}
                        fontSize={"0.75rem"}
                      >
                        {errors.totalAnnualIncomeCoborrower.message}
                      </Typography>
                    )}
                    <Divider />
                    <Box mt={3}>
                      <Typography
                        variant="h6"
                        color="black"
                        fontStyle={"initial"}
                      >
                        Total Household Income:
                        {dollarFormatter.format(totalHouseholdIncome)}
                      </Typography>
                    </Box>
                    <Divider />
                  </>
                )}
                {!isLoggedIn && (
                  <Controller
                    control={control}
                    name="password"
                    render={({ field }) => (
                      <CPasswordField
                        label="Create Password"
                        placeholder="Type in here"
                        fullWidth
                        autoComplete="new-password"
                        helperText={
                          asCaOcuppancyType && isCornerstonePL ? (
                            <>
                              <div>{passwordMsgHelper}</div>
                              <div>
                                California residents: learn about your CCPA
                                privacy rights by reviewing our{" "}
                                <Link
                                  color={theme.palette.primary.main}
                                  textAlign={"justify"}
                                  sx={{
                                    cursor: "pointer",
                                    textDecoration: "none",
                                  }}
                                  href={
                                    privateLabel?.footerPolicy?.privacyTerms
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  CCPA disclosure
                                </Link>
                                .
                              </div>
                            </>
                          ) : (
                            passwordMsgHelper
                          )
                        }
                        {...field}
                        error={errors?.password?.message}
                      />
                    )}
                  />
                )}
                <List
                  disablePadding
                  sx={{ fontSize: { xs: "1rem", md: "1.4rem" } }}
                >
                  <PrivateLabelCheckboxes
                    privateLabel={privateLabel}
                    form={form}
                  />
                </List>

                <Typography
                  my={"1rem"}
                  color={theme.palette.text.secondary}
                  variant="subtitle2"
                  textAlign={"justify"}
                >
                  {
                    privateLabel?.borrowerPortalConfiguration
                      ?.customHomePageContent?.consentText
                  }
                </Typography>
                {messageRestrictedState && (
                  <Typography
                    my={"1rem"}
                    color={"red"}
                    variant="subtitle2"
                    textAlign={"justify"}
                  >
                    {messageRestrictedState}
                  </Typography>
                )}
                {isCornerstonePL ? (
                  <>
                    <CButton
                      fullWidth
                      data-cy="iAgree"
                      type="submit"
                      variant="contained"
                      disabled={disableSubmit}
                      sx={{
                        mt: 4,
                        mb: 2,
                        fontSize: 18,
                        textTransform: "inherit",
                        alignItems: "flex-start",
                        textAlign: "left",
                        justifyContent: "space-between",
                        display: "flex",
                        position: "relative",
                      }}
                      loading={
                        form.formState.isSubmitting || user.isGettingLoggedIn
                      }
                    >
                      LET'S GET STARTED
                      <PlayArrowIcon
                        style={{
                          fontSize: 35,
                          position: "absolute",
                          right: 20,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    </CButton>
                    <Typography
                      my={"1rem"}
                      color={theme.palette.text.secondary}
                      variant="subtitle2"
                      textAlign={"justify"}
                    >
                      For technical support or any additional questions, please
                      contact {privateLabel?.contactEmail}
                    </Typography>
                  </>
                ) : (
                  <CButton
                    fullWidth
                    data-cy="iAgree"
                    type="submit"
                    variant="contained"
                    disabled={disableSubmit}
                    sx={{
                      mt: 4,
                      mb: 2,
                      fontSize: 18,
                      textTransform: "inherit",
                    }}
                    loading={
                      form.formState.isSubmitting || user.isGettingLoggedIn
                    }
                  >
                    I Agree
                  </CButton>
                )}
              </form>
            </Box>
          </Box>
        </Fragment>
      </Container>
      <CVerificationDialog
        phoneNumber={form.watch("phoneNumber")}
        open={form.openedDialog === "verify"}
        defaultVerificationType={defaultVerificationType}
        email={form.watch("email")}
        onConfirmed={() => {
          form.setOpenedDialog(undefined);
          user.login(form.watch("email"), form.watch("password"));
        }}
        phoneType={form?.checkPhoneValidation?.phoneTypeBorrower}
      />
      <CDialog
        icon="cancel"
        description={
          state === "UT" || state === "NV" || state === "NY"
            ? "We are unable to lend in this state at this time."
            : ""
        }
        open={form.isRestrictedState}
      >
        <CButton
          data-cy="continueRegister"
          fullWidth
          variant="contained"
          onClick={() => {
            form.setIsRestrictedState(false);
          }}
        >
          Continue
        </CButton>
      </CDialog>
      <CDialog
        icon="warning"
        description={form.emailAlreadyUseOnPL}
        open={form.openedDialog === "EMAIL_IN_USE_ON_PL"}
      >
        <CButton
          data-cy="continueRegister"
          fullWidth
          variant="contained"
          onClick={() => {
            if (!isLoggedIn) {
              user.login(form.watch("email"), form.watch("password"));
            } else {
              navigate("/loans", { replace: true });
            }

            form.setOpenedDialog(undefined);
          }}
        >
          Continue
        </CButton>
      </CDialog>
      <CDialog
        icon="warning"
        description={form.errorMessage}
        open={form.openedDialog === "EXISTANT_USER_NOT_LOGGED"}
      >
        <CButton
          fullWidth
          variant="contained"
          onClick={() => {
            navigate("/login", { replace: true });

            form.setOpenedDialog(undefined);
          }}
        >
          Login
        </CButton>
      </CDialog>
      <CDialog
        icon="cancel"
        description={
          userAlreadyExists
            ? "User already exists, please login here."
            : form.errorCode
              ? `${form.errorMessage} ${
                  form.errorMessage?.includes("support@nftydoor.com") ||
                  privateLabel
                    ? " "
                    : "Contact us at support@nftydoor.com for any questions."
                }`
              : form.errorMessage
        }
        open={
          form.openedDialog === "error" &&
          form.errorCode !== "BORROWER_IS_ARCHIVED"
        }
      >
        <CButton
          fullWidth
          variant="contained"
          onClick={() =>
            userAlreadyExists
              ? navigate("/login", { replace: true })
              : form.setOpenedDialog(undefined)
          }
        >
          {userAlreadyExists ? "Login" : "Accept"}
        </CButton>
        {userAlreadyExists && (
          <CButton
            fullWidth
            variant="outlined"
            onClick={() => form.setOpenedDialog(undefined)}
            sx={{ mt: 2 }}
          >
            Go back
          </CButton>
        )}
      </CDialog>
      <CDialog
        icon="cancel"
        description={form.errorMessage}
        error={form.errorCode}
        open={
          form.openedDialog === "error" &&
          form.errorCode === "BORROWER_IS_ARCHIVED"
        }
      >
        <CButton
          fullWidth
          variant="contained"
          onClick={() =>
            userAlreadyExists
              ? navigate("/login", { replace: true })
              : form.setOpenedDialog(undefined)
          }
        >
          Accept
        </CButton>
      </CDialog>
      <ConfirmValuesDialog form={form} invite={invite} />
      <CTermsDialog
        open={form.openedDialog === "terms"}
        handleClose={() => form.setOpenedDialog(undefined)}
        privateLabel={privateLabel}
      />
      <CCommunicationsDialog
        open={form.openedDialog === "communications"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
    </Fragment>
  );
};

export default Form;
